import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Loader2, LogOut, RefreshCw, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface PortalAuthGuardProps {
  /** Underlying auth-state flags */
  loading: boolean;
  profileLoaded: boolean;
  user: unknown;
  profile: unknown;
  /** Sign out + redirect to portal login */
  onSignOut: () => void | Promise<void>;
  /** Branding shown on full-page loading screen */
  brandIcon: ReactNode;
  brandLabel: string;
  /** Copy shown when profile fails to load */
  accessDeniedMessage?: string;
  retryWaitedMessage?: string;
  /** Optional ms to wait before showing the "retry" affordance (default 8000) */
  waitMs?: number;
  /** Rendered once profile is ready */
  children: ReactNode;
  /** Skeleton shown inside the layout while profile resolves but session exists */
  contentFallback?: ReactNode;
}

export function PortalLoadingSkeleton() {
  return (
    <div className="p-6 space-y-4 flex-1">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-96 opacity-60" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl mt-4" />
    </div>
  );
}

/**
 * Unified loading + access-denied surface for all portal layouts.
 * Returns either:
 *   - the full-page loading screen,
 *   - the access-denied screen,
 *   - or `children` (which receives `contentReady=true` semantics from the parent).
 *
 * The parent layout renders the shell (header/sidebar) and passes the page
 * content + `contentReady` skeleton in.
 */
export function PortalAuthGuard({
  loading,
  profileLoaded,
  user,
  profile,
  onSignOut,
  brandIcon,
  brandLabel,
  accessDeniedMessage = 'Your account does not have access to this portal. Contact your administrator.',
  retryWaitedMessage = 'Unable to load your profile. Please try again or contact support.',
  waitMs = 8000,
  children,
}: PortalAuthGuardProps) {
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  useEffect(() => {
    if (!loading && profileLoaded && user && !profile) {
      const t = setTimeout(() => setWaitedTooLong(true), waitMs);
      return () => clearTimeout(t);
    }
    setWaitedTooLong(false);
  }, [loading, profileLoaded, user, profile, waitMs]);

  const handleSignOut = useCallback(async () => {
    await onSignOut();
  }, [onSignOut]);

  // First-load: no session yet → show full-page branded spinner
  if ((loading || !profileLoaded) && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="opacity-60">{brandIcon}</div>
          <Loader2 className="w-7 h-7 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading {brandLabel}...</p>
        </div>
      </div>
    );
  }

  // Session exists but profile failed to load → access-denied surface
  if (profileLoaded && user && !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center max-w-md px-6">
          <div className="rounded-full bg-destructive/10 p-6">
            <Shield className="w-12 h-12 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
          <p className="text-muted-foreground">
            {waitedTooLong ? retryWaitedMessage : accessDeniedMessage}
          </p>
          <div className="flex gap-3">
            {waitedTooLong && (
              <Button variant="outline" onClick={() => window.location.reload()}>
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
